import IconMdi from '@components/IconMdi';
import { mdiClose } from '@mdi/js';
import type { Table } from '@tanstack/react-table';
import clsx from 'clsx';

interface ColumnVisibilityControlsProps<TData> {
  table: Table<TData>;
  onClose?: () => void;
}

export const ColumnVisibilityControls = <TData,>({ table, onClose }: ColumnVisibilityControlsProps<TData>) => {
  const allLeafColumns = table.getAllLeafColumns();
  const controllableColumns = allLeafColumns.filter((column) => column.getCanHide());
  const visibleColumnsCount = controllableColumns.filter((column) => column.getIsVisible()).length;

  const areAllControllableColumnsVisible = visibleColumnsCount === controllableColumns.length;
  const handleToggleAllControllableColumns = () => {
    const shouldShowAll = !areAllControllableColumnsVisible;
    const currentVisibility = table.getState().columnVisibility;

    const newVisibility = { ...currentVisibility };
    controllableColumns.forEach((column) => {
      newVisibility[column.id] = shouldShowAll;
    });

    table.setColumnVisibility(newVisibility);
  };

  const handleResetToDefault = () => table.resetColumnVisibility();

  return (
    <>
      <div className="mb-3 flex items-center justify-between border-b border-gray-200 pb-3">
        <h3 className="text-sm font-medium text-gray-900">Column Visibility</h3>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 focus:outline-none" aria-label="Close">
            <IconMdi path={mdiClose} size={0.8} />
          </button>
        )}
      </div>

      <div className="mb-3 text-xs text-gray-500">
        {visibleColumnsCount} of {controllableColumns.length} columns visible
      </div>

      <div className="mb-3 border-b border-gray-200 pb-3">
        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={areAllControllableColumnsVisible}
            onChange={handleToggleAllControllableColumns}
            className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
          />
          <span>Toggle All</span>
        </label>
      </div>

      <div className="max-h-64 space-y-2 overflow-y-auto">
        {allLeafColumns.map((column) => {
          if (!column.getCanHide()) return null;
          const headerText = typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id;

          return (
            <label
              key={column.id}
              className={clsx(
                'flex items-center space-x-2 text-sm',
                column.getIsVisible() ? 'text-gray-700' : 'text-gray-400',
              )}
            >
              <input
                type="checkbox"
                checked={column.getIsVisible()}
                onChange={column.getToggleVisibilityHandler()}
                className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
              />
              <span className="truncate" title={headerText}>
                {headerText}
              </span>
            </label>
          );
        })}
      </div>
      <div className="mt-4 border-t border-gray-200 pt-3">
        <button
          onClick={handleResetToDefault}
          className="focus:ring-primary-500 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-offset-1 focus:outline-none"
        >
          Reset to Default
        </button>
      </div>
    </>
  );
};
